import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { loginUser } from "../../api";
import { toast, Toaster } from "react-hot-toast";
import { 
  UserIcon, 
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowRightOnRectangleIcon,
  KeyIcon,
  CheckBadgeIcon,
  UserCircleIcon
} from '@heroicons/react/24/outline';

const Login = () => {
  const [step, setStep] = useState(1); // 1: credentials, 2: PIN
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    pin: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [userResponse, setUserResponse] = useState(null);
  const navigate = useNavigate();

  const DEMO_PIN = "9860"; // Demo PIN

  const validateStep1 = () => {
    const newErrors = {};
    
    if (!formData.username.trim()) {
      newErrors.username = "Username is required";
    }
    
    if (!formData.password.trim()) {
      newErrors.password = "Password is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    
    if (!formData.pin.trim()) {
      newErrors.pin = "PIN is required";
    } else if (formData.pin.length !== 4) {
      newErrors.pin = "PIN must be 4 digits";
    } else if (!/^\d+$/.test(formData.pin)) {
      newErrors.pin = "PIN must be numeric";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleStep1Submit = async (e) => {
    e.preventDefault();
    
    if (!validateStep1()) {
      toast.error("Please fix all errors");
      return;
    }

    setLoading(true);

    try {
      const response = await loginUser({ 
        username: formData.username, 
        password: formData.password 
      });
      
      if (response.message === "Login successful") {
        setUserResponse(response);
        setStep(2); // Move to PIN step
        toast.success("Credentials verified! Enter PIN to continue.");
      } else {
        toast.error(response.message || "Authentication failed");
        setErrors({ username: response.message });
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error("Connection failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleStep2Submit = async (e) => {
    e.preventDefault();
    
    if (!validateStep2()) {
      toast.error("Please enter valid PIN");
      return;
    }

    // Verify PIN
    if (formData.pin !== DEMO_PIN) {
      toast.error("Invalid PIN! Access Denied.");
      setErrors({ pin: "Incorrect PIN" });
      return;
    }

    setLoading(true);

    // Store user data and redirect
    setTimeout(() => {
      sessionStorage.setItem("user", JSON.stringify(userResponse.user));
      toast.success("Login successful!");
      
      setTimeout(() => {
        if (userResponse.user.role === "admin") {
          navigate("/admin/");
        } else {
          navigate("/exam/");
        }
      }, 500);
      setLoading(false);
    }, 800);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'pin') {
      const cleanedValue = value.replace(/\D/g, '');
      if (cleanedValue.length <= 4) {
        setFormData((prev) => ({ ...prev, [name]: cleanedValue }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleBackToStep1 = () => {
    setStep(1);
    setFormData({ ...formData, pin: "" });
    setErrors({});
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 relative overflow-hidden">
      <Toaster position="top-right" toastOptions={{
        style: { background: '#fff', color: '#363636', boxShadow: '0 10px 40px rgba(0,0,0,0.1)', borderRadius: '12px' },
        duration: 4000
      }} />
      
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-200/40 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-200/40 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 w-80 h-80 bg-indigo-200/40 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
      </div>

      {/* Main Content */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          
          {/* Login Card */}
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            
            {/* Header */}
            <div className="px-6 py-5 border-b border-gray-100">
              <div className="flex items-center space-x-2.5">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-md">
                  <UserCircleIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-800">
                    {step === 1 ? 'Welcome Back' : 'Security Verification'}
                  </h1>
                  <p className="text-xs text-gray-500">
                    {step === 1 ? 'Sign in to your account' : 'Enter your security PIN'}
                  </p>
                </div>
              </div>
            </div>

            {/* Progress Indicator */}
            <div className="px-6 pt-5">
              <div className="flex items-center justify-center space-x-2">
                <div className={`flex items-center ${step === 1 ? 'text-blue-600' : 'text-green-600'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm ${
                    step === 1 ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'
                  }`}>
                    {step === 1 ? '1' : '✓'}
                  </div>
                  <span className="ml-2 text-xs font-medium">Credentials</span>
                </div>
                <div className={`w-16 h-0.5 ${step === 2 ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
                <div className={`flex items-center ${step === 2 ? 'text-blue-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm ${
                    step === 2 ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'
                  }`}>
                    2
                  </div>
                  <span className="ml-2 text-xs font-medium">PIN</span>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="px-6 py-6">
              
              {/* Step 1: Username & Password */}
              {step === 1 && (
                <form onSubmit={handleStep1Submit} className="space-y-4">
                  
                  {/* Username */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Username <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <UserIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        name="username"
                        className={`w-full pl-10 pr-4 py-3 text-sm bg-gray-50 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${
                          errors.username ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                        value={formData.username}
                        onChange={handleChange}
                        placeholder="Enter your username"
                      />
                    </div>
                    {errors.username && (
                      <p className="mt-1.5 text-xs text-red-600">⚠ {errors.username}</p>
                    )}
                  </div>

                  {/* Password */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <LockClosedIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        className={`w-full pl-10 pr-10 py-3 text-sm bg-gray-50 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${
                          errors.password ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Enter your password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showPassword ? (
                          <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                        ) : (
                          <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                        )}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="mt-1.5 text-xs text-red-600">⚠ {errors.password}</p>
                    )}
                  </div>

                  {/* Continue Button */}
                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3.5 rounded-lg font-semibold hover:shadow-lg hover:scale-[1.01] active:scale-[0.99] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Verifying...</span>
                        </>
                      ) : (
                        <>
                          <ArrowRightOnRectangleIcon className="h-5 w-5" />
                          <span>Continue</span>
                        </>
                      )}
                    </button>
                  </div>

             
                </form>
              )}

              {/* Step 2: Security PIN */}
              {step === 2 && (
                <form onSubmit={handleStep2Submit} className="space-y-4">
                  
                  {/* User Info Display */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-full flex items-center justify-center">
                        <UserIcon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-800">
                          {formData.username}
                        </p>
                        <p className="text-xs text-gray-600">Verified ✓</p>
                      </div>
                    </div>
                  </div>

                  {/* Security PIN */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Security PIN <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <KeyIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type={showPin ? "text" : "password"}
                        name="pin"
                        className={`w-full pl-10 pr-10 py-3 text-sm bg-gray-50 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all tracking-widest font-mono text-center text-xl ${
                          errors.pin ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                        value={formData.pin}
                        onChange={handleChange}
                        placeholder="••••"
                        maxLength={4}
                        inputMode="numeric"
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={() => setShowPin(!showPin)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showPin ? (
                          <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                        ) : (
                          <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                        )}
                      </button>
                    </div>
                    {errors.pin ? (
                      <p className="mt-1.5 text-xs text-red-600">⚠ {errors.pin}</p>
                    ) : (
                      <p className="mt-1.5 text-xs text-blue-600 flex items-center space-x-1">
                     
                      </p>
                    )}
                  </div>

                 

                  {/* Buttons */}
                  <div className="pt-2 space-y-2">
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3.5 rounded-lg font-semibold hover:shadow-lg hover:scale-[1.01] active:scale-[0.99] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Signing In...</span>
                        </>
                      ) : (
                        <>
                          <CheckBadgeIcon className="h-5 w-5" />
                          <span>Verify & Sign In</span>
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={handleBackToStep1}
                      className="w-full bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-200 hover:scale-[1.01] active:scale-[0.99] transition-all"
                    >
                      Back to Login
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes blob { 0%, 100% { transform: translate(0, 0) scale(1); } 33% { transform: translate(30px, -50px) scale(1.1); } 66% { transform: translate(-20px, 20px) scale(0.9); } }
        .animate-blob { animation: blob 7s infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
        .animation-delay-4000 { animation-delay: 4s; }
      `}</style>
    </div>
  );
};

export default Login;
